import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getRequestAuthToken } from '../auth/appliedAuthToken';

interface BrandConfig {
  logo_url?: string;
  logo_dark_url?: string;
  primary_color?: string;
}

export const useBranding = () => {
  const { user } = useAuth();
  const [brandConfig, setBrandConfig] = useState<BrandConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBrandConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getRequestAuthToken();
      if (!token || !user?.org_id) {
        setBrandConfig(null);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branding/logo`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const config = await response.json();
        setBrandConfig(config);
      } else {
        setBrandConfig(null);
      }
    } catch (err) {
      console.warn('Failed to fetch brand config:', err);
      setError('branding:errorLoad');
      setBrandConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const uploadLogo = async (logoFile: File, darkLogoFile?: File) => {
    try {
      setError(null);
      
      const token = getRequestAuthToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const formData = new FormData();
      formData.append('logo', logoFile);
      if (darkLogoFile) {
        formData.append('logo_dark', darkLogoFile);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branding/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'branding:errorUpload');
      }

      const config = await response.json();
      setBrandConfig(config);
      return config;
    } catch (err) {
      const errorCode = err instanceof Error ? err.message : 'branding:errorUpload';
      setError(errorCode);
      throw err;
    }
  };

  const updateTheme = async (primaryColor: string) => {
    try {
      setError(null);
      
      const token = getRequestAuthToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branding/theme`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ primary_color: primaryColor }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'branding:errorTheme');
      }

      const config = await response.json();
      setBrandConfig(config);
      
      // Update CSS variables
      document.documentElement.style.setProperty('--color-primary', primaryColor);
      
      return config;
    } catch (err) {
      const errorCode = err instanceof Error ? err.message : 'branding:errorTheme';
      setError(errorCode);
      throw err;
    }
  };

  useEffect(() => {
    fetchBrandConfig();
  }, [user?.org_id]);

  useEffect(() => {
    // Apply primary color to CSS variables when brand config loads
    if (brandConfig?.primary_color) {
      document.documentElement.style.setProperty('--color-primary', brandConfig.primary_color);
    }
  }, [brandConfig]);

  return {
    brandConfig,
    loading,
    error,
    refreshBrandConfig: fetchBrandConfig,
    uploadLogo,
    updateTheme,
  };
};